import PropTypes from 'prop-types';
import { shortCurrencyFormatter } from 'components/KPIs/helpers';

const formatNumber = (value, options = {}) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
    return Number(value).toLocaleString(undefined, options);
};

const DetailRow = ({ label, children }) => (
    <div className="customer-detail__row">
        <span className="customer-detail__row-label">{label}</span>
        <span className="customer-detail__row-value">{children}</span>
    </div>
);

DetailRow.propTypes = {
    label: PropTypes.string.isRequired,
    children: PropTypes.node,
};

const combine = (summary, detail) => ({
    ...(summary || {}),
    ...(detail || {}),
});

const buildAddress = (detail) => {
    if (!detail) return null;
    const parts = [detail.address_1, detail.address_2, detail.city, detail.state, detail.zip_code]
        .filter(Boolean)
        .map((part) => String(part).trim());
    if (!parts.length) return null;
    return parts.join(' · ');
};

export default function CustomerDetailView({ summary, detail, loading = false, error = null }) {
    const combined = combine(summary, detail);

    if (!summary && !detail) {
        return (
            <div className="customer-detail customer-detail--empty" role="status" aria-live="polite">
                No customer data available for this period.
            </div>
        );
    }

    const lanesSource = Array.isArray(detail?.lane_stats)
        ? detail.lane_stats
        : Array.isArray(detail?.lanes)
            ? detail.lanes
            : Array.isArray(summary?.lanes)
                ? summary.lanes
                : [];
    const lanes = lanesSource.map((lane) => ({
        ...lane,
        lane_name: lane?.lane_name || lane?.lane,
    }));

    const deriveLaneMargin = (lane) => {
        if (lane?.margin != null) return lane.margin;
        const profit = Number(lane?.profit ?? 0);
        const revenue = Number(lane?.revenue ?? 0);
        if (!revenue) return null;
        return (profit / revenue) * 100;
    };

    const address = buildAddress(detail);
    const totalLoads = (() => {
        if (combined.load_count != null) return combined.load_count;
        if (combined.order_count != null) return combined.order_count;
        return lanes.reduce((sum, lane) => sum + (Number(lane?.load_count || lane?.orders) || 0), 0);
    })();

    const contactSnapshot = [
        detail?.phone ? { label: 'Phone', value: detail.phone } : null,
        detail?.sales_person ? { label: 'Sales Rep', value: detail.sales_person } : null,
        address ? { label: 'Address', value: address } : null,
    ].filter(Boolean);

    const billingSnapshot = [
        detail?.billing_status ? { label: 'Status', value: detail.billing_status } : null,
        detail?.payment_terms ? { label: 'Terms', value: detail.payment_terms } : null,
        detail?.credit_limit ? { label: 'Credit Limit', value: detail.credit_limit } : null,
        detail?.credit_avl ? { label: 'Credit Available', value: detail.credit_avl } : null,
        detail?.payment_overdue ? { label: 'Overdue Balance', value: formatNumber(detail.payment_overdue) } : null,
    ].filter(Boolean);

    const logisticsSnapshot = [
        detail?.min_hazmat ? { label: 'Min Hazmat', value: detail.min_hazmat } : null,
        detail?.min_dock_level ? { label: 'Min Dock Level', value: detail.min_dock_level } : null,
        detail?.timezone ? { label: 'Timezone', value: detail.timezone } : null,
    ].filter(Boolean);

    const miscSnapshot = [
        detail?.customer_code ? { label: 'Customer Code', value: detail.customer_code } : null,
        detail?.alpha_code ? { label: 'Alpha Code', value: detail.alpha_code } : null,
        detail?.created_at ? { label: 'Created', value: new Date(detail.created_at).toLocaleDateString() } : null,
        detail?.updated_at ? { label: 'Updated', value: new Date(detail.updated_at).toLocaleDateString() } : null,
    ].filter(Boolean);

    const notice = loading
        ? `Loading ${summary?.company_name || 'customer'} data…`
        : error
            ? `Failed to load customer details. ${typeof error === 'string' ? error : 'Please try again.'}`
            : null;

    const noticeType = loading ? 'info' : error ? 'error' : null;

    return (
        <div
            className={`customer-detail${loading ? ' customer-detail--loading' : ''}${error ? ' customer-detail--error' : ''}`}
            data-customer-id={combined.id}
        >
            <header className="customer-detail__header">
                <div className="customer-detail__title-group">
                    <h4 className="customer-detail__title">{combined.company_name}</h4>
                    {(() => {
                        const subtitleParts = [
                            combined.customer_code || detail?.customer_code ? `Code: ${combined.customer_code || detail?.customer_code}` : null,
                            detail?.parent_id ? `Parent: ${detail.parent_id}` : null,
                            combined.order_count != null ? `Orders: ${formatNumber(combined.order_count)}` : null,
                            combined.miles != null ? `Miles: ${formatNumber(combined.miles)}` : null,
                            totalLoads != null ? `Loads: ${formatNumber(totalLoads, { maximumFractionDigits: 0 })}` : null,
                        ].filter(Boolean);
                        if (!subtitleParts.length) return null;
                        return <p className="customer-detail__subtitle">{subtitleParts.join(' · ')}</p>;
                    })()}
                </div>
            </header>

            {(contactSnapshot.length > 0 || billingSnapshot.length > 0 || logisticsSnapshot.length > 0 || miscSnapshot.length > 0 || notice) && (
                <section className="customer-detail__meta">
                    <div className="customer-detail__meta-columns">
                        {contactSnapshot.length > 0 && (
                            <div className="customer-detail__meta-column">
                                <h5 className="customer-detail__section-title">Contact</h5>
                                <div className="customer-detail__meta-grid">
                                    {contactSnapshot.map((item) => (
                                        <DetailRow key={item.label} label={item.label}>{item.value}</DetailRow>
                                    ))}
                                </div>
                            </div>
                        )}
                        {billingSnapshot.length > 0 && (
                            <div className="customer-detail__meta-column">
                                <h5 className="customer-detail__section-title">Billing</h5>
                                <div className="customer-detail__meta-grid">
                                    {billingSnapshot.map((item) => (
                                        <DetailRow key={item.label} label={item.label}>{item.value}</DetailRow>
                                    ))}
                                </div>
                            </div>
                        )}
                        {logisticsSnapshot.length > 0 && (
                            <div className="customer-detail__meta-column">
                                <h5 className="customer-detail__section-title">Logistics</h5>
                                <div className="customer-detail__meta-grid">
                                    {logisticsSnapshot.map((item) => (
                                        <DetailRow key={item.label} label={item.label}>{item.value}</DetailRow>
                                    ))}
                                </div>
                            </div>
                        )}
                        {miscSnapshot.length > 0 && (
                            <div className="customer-detail__meta-column">
                                <h5 className="customer-detail__section-title">Misc</h5>
                                <div className="customer-detail__meta-grid">
                                    {miscSnapshot.map((item) => (
                                        <DetailRow key={item.label} label={item.label}>{item.value}</DetailRow>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {notice && (
                        <div
                            className={`customer-detail__notice${noticeType ? ` customer-detail__notice--${noticeType}` : ''}`}
                            role={error ? 'alert' : 'status'}
                            aria-live={error ? 'assertive' : 'polite'}
                        >
                            {notice}
                        </div>
                    )}
                </section>
            )}

            <section className="customer-detail__lanes">
                <h5 className="customer-detail__section-title">Lanes breakdown</h5>

                {lanes.length === 0 ? (
                    <div className="customer-detail__empty">No lane level data for this customer.</div>
                ) : (
                    <div className="customer-detail__table-wrapper">
                        <table className="customer-detail__lane-table">
                            <thead>
                                <tr>
                                    <th>Lane</th>
                                    <th>Loads</th>
                                    <th>Miles</th>
                                    <th>Revenue</th>
                                    <th>Profit</th>
                                    <th>Margin</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lanes.map((lane, idx) => {
                                    const loads = Number(lane?.load_count ?? lane?.orders ?? 0);
                                    const margin = deriveLaneMargin(lane);
                                    return (
                                        <tr key={lane?.lane_id ?? `${lane?.lane_name || 'lane'}-${idx}`}>
                                            <td className="customer-detail__lane-col">
                                                <span className="customer-detail__lane-name">{lane?.lane_name || 'Unknown lane'}</span>

                                            </td>
                                            <td>{formatNumber(loads, { maximumFractionDigits: 0 })}</td>
                                            <td>{formatNumber(lane?.miles)}</td>
                                            <td>{shortCurrencyFormatter(lane?.revenue ?? 0)}</td>
                                            <td>{shortCurrencyFormatter(lane?.profit ?? 0)}</td>
                                            <td>{margin != null ? `${Math.round(margin)}%` : '—'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

        </div>
    );
}

CustomerDetailView.propTypes = {
    summary: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        company_name: PropTypes.string,
        order_count: PropTypes.number,
        miles: PropTypes.number,
        revenue: PropTypes.number,
        profit: PropTypes.number,
        margin: PropTypes.number,
        lanes: PropTypes.arrayOf(
            PropTypes.shape({
                lane_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                lane_name: PropTypes.string,
                lane: PropTypes.string,
                load_count: PropTypes.number,
                orders: PropTypes.number,
                miles: PropTypes.number,
                revenue: PropTypes.number,
                profit: PropTypes.number,
                margin: PropTypes.number,
            })
        ),
    }),
    detail: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        company_name: PropTypes.string,
        order_count: PropTypes.number,
        miles: PropTypes.number,
        revenue: PropTypes.number,
        profit: PropTypes.number,
        margin: PropTypes.number,
        lane_stats: PropTypes.arrayOf(
            PropTypes.shape({
                lane_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                lane_name: PropTypes.string,
                lane: PropTypes.string,
                load_count: PropTypes.number,
                miles: PropTypes.number,
                revenue: PropTypes.number,
                profit: PropTypes.number,
                margin: PropTypes.number,
            })
        ),
        lanes: PropTypes.arrayOf(
            PropTypes.shape({
                lane_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                lane_name: PropTypes.string,
                lane: PropTypes.string,
                load_count: PropTypes.number,
                orders: PropTypes.number,
                miles: PropTypes.number,
                revenue: PropTypes.number,
                profit: PropTypes.number,
                margin: PropTypes.number,
            })
        ),
    }),
    loading: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};
